<script setup>
import { useCommonInputProps } from '@/shared/composables/forms/common-input-props.composable.js'
import { useInputHandlers } from '@/shared/composables/forms/input-handlers.composable.js'

// Props
const properties = defineProps({
  ...useCommonInputProps(), // Merge common props
  modelValue: { type: [String, Number], default: '' },
  type: { type: String, default: 'text' },
  iconPos: { type: String, default: 'left' },
  icon: { type: String, default: '' },
  labelClass: { type: String, default: '' },
  pt: { type: [Object, String], default: null },
  min: { type: Number, default: 0 },
  max: { type: Number, default: null },
  showButtons: { type: Boolean, default: false },
  buttonLayout: { type: String, default: 'stacked' },
  maxLength: { type: Number, default: undefined },
  clickableIcon: { type: Boolean, default: false },
})

const emit = defineEmits([
  'update:modelValue',
  'input',
  'focus',
  'blur',
  'enter',
  'iconClicked',
])

const {
  inputId,
  inputValue,
  handleInput,
  handleFocus,
  handleBlur,
  handleEnter,
} = useInputHandlers(properties, emit)

const isLeft = computed(() => properties.iconPos === 'left')

const computedInputClass = computed(() => {
  return properties.readonly
    ? `${properties.inputClass} read-only:bg-dss-secondary-50 read-only:pointer-events-none`
    : properties.inputClass
})

function handleTextInput(event) {
  if (properties.maxLength && event.target.value.length > properties.maxLength) {
    event.target.value = event.target.value.slice(0, properties.maxLength)
  }
  handleInput(event)
}
</script>

<template>
  <div class="flex flex-col gap-1">
    <label
      v-if="label"
      class="text-sm text-dss-surface-900"
      :class="labelClass"
      :for="inputId"
      :data-testid="`dss-input-text-label-${name}`"
    >
      {{ label }}
      <span
        v-if="required"
        class="text-red-600"
        :data-testid="`dss-input-text-required-${name}`"
      >*</span>
    </label>

    <template v-if="icon">
      <IconField>
        <InputIcon
          v-if="isLeft"
          class="pi"
          :class="icon"
          :clickable-icon="properties.clickableIcon"
          @click="properties.clickableIcon && emit('iconClicked')"
        />
        <InputNumber
          v-if="type === 'number'"
          v-model="inputValue"
          :name="name"
          :placeholder="placeholder"
          :disabled="disabled"
          :readonly="readonly"
          :invalid="invalid"
          :class="computedInputClass"
          :min="min"
          :max="max"
          :pt="pt"
          :show-buttons="showButtons"
          :button-layout="buttonLayout"
          :data-testid="`dss-input-text-${name}`"
          fluid
          @input="handleTextInput"
          @focus="handleFocus"
          @blur="handleBlur"
          @keyup.enter="handleEnter"
        />
        <InputText
          v-else
          v-model="inputValue"
          :name="name"
          :type="type"
          :placeholder="placeholder"
          :disabled="disabled"
          :readonly="readonly"
          :invalid="invalid"
          :class="computedInputClass"
          class="w-full"
          :pt="pt"
          :data-testid="`dss-input-text-${name}`"
          @input="handleTextInput"
          @focus="handleFocus"
          @blur="handleBlur"
          @keyup.enter="handleEnter"
        />
        <InputIcon
          v-if="!isLeft"
          class="pi"
          :data-testid="`input-icon-${name}`"
          :class="icon"
          :clickable-icon="properties.clickableIcon"
          @click="properties.clickableIcon && emit('iconClicked')"
        />
      </IconField>
    </template>

    <template v-else>
      <InputNumber
        v-if="type === 'number'"
        v-model="inputValue"
        :name="name"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :invalid="invalid"
        :class="computedInputClass"
        :min="min"
        :max="max"
        :pt="pt"
        :show-buttons="showButtons"
        :button-layout="buttonLayout"
        :data-testid="`dss-input-text-${name}`"
        fluid
        @input="handleTextInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keyup.enter="handleEnter"
      />
      <InputText
        v-else
        v-model="inputValue"
        :name="name"
        :type="type"
        :maxlength="maxLength"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :invalid="invalid"
        :class="computedInputClass"
        class="w-full"
        :pt="pt"
        :data-testid="`dss-input-text-${name}`"
        @input="handleTextInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keyup.enter="handleEnter"
      />

      <small
        v-if="invalid"
        class="text-red-500"
        :data-testid="`dss-input-text-error-message-${name}`"
      >{{ errorMessage }}</small>

      <small
        v-if="helper && !invalid"
        class="text-xs text-dss-surface-900"
      >{{ helper }}</small>

      <small
        v-if="maxLength"
        class="text-xs text-dss-surface-700"
        :class="{ 'text-red-500': inputValue?.length === maxLength }"
      >{{ inputValue?.length || 0 }}/{{ maxLength }}</small>
    </template>
  </div>
</template>
